import { useState, useMemo, useCallback } from "react";
import {
  getDateRange,
  getDateRangeOptions,
  formatDateString,
} from "../../../../../services/consults-utils";
import { trackEvent } from "../../../../../utils/mixpanel";
import { TDateRangeOption } from "../DateRangePicker";

/**
 * Manages date range selection and navigation
 */
export const useDateRange = (session: any) => {
  const [viewMode, setViewMode] = useState<"daily" | "weekly">("daily");
  const [rangeOffset, setRangeOffset] = useState(0);

  // Calculate current date range
  const { startDate, endDate, viewLabel, isNextWeek } = useMemo(
    () => getDateRange(viewMode, rangeOffset),
    [viewMode, rangeOffset],
  );

  const rangeOptions = useMemo(() => getDateRangeOptions(viewMode), [viewMode]);

  const minOffset = useMemo(
    () =>
      rangeOptions.length > 0
        ? rangeOptions[rangeOptions.length - 1].offset
        : -30,
    [rangeOptions],
  );

  // Store formatted dates for API calls
  const formattedStartDate = formatDateString(startDate);
  const formattedEndDate = formatDateString(endDate);

  const [currentDateRange, setCurrentDateRange] = useState({
    start: formattedStartDate,
    end: formattedEndDate,
  });

  // Update range offset with optional tracking
  const updateRangeOffset = useCallback(
    (
      valueOrUpdater: number | ((prev: number) => number),
      eventName?: string,
    ) => {
      setRangeOffset((prev) => {
        const nextRaw =
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(prev)
            : valueOrUpdater;
        const clampedOffset = Math.min(0, Math.max(nextRaw, minOffset));

        if (clampedOffset === prev) return prev;

        if (eventName) {
          const { startDate: rangeStart, endDate: rangeEnd } = getDateRange(
            viewMode,
            clampedOffset,
          );
          trackEvent(
            eventName,
            {
              "Start Date": formatDateString(rangeStart),
              "End Date": formatDateString(rangeEnd),
              "View Mode": viewMode,
              Offset: clampedOffset,
            },
            session,
          );
        }

        return clampedOffset;
      });
    },
    [minOffset, session, viewMode],
  );

  // Shift range by delta (e.g., next/previous week)
  const handleShiftRange = useCallback(
    (delta: number) => {
      const eventName =
        viewMode === "weekly"
          ? "Consult Week Navigated"
          : "Consult Day Navigated";
      updateRangeOffset((prev) => prev + delta, eventName);
    },
    [updateRangeOffset, viewMode],
  );

  // Select specific range from picker
  const handleRangeSelect = useCallback(
    (option: TDateRangeOption) => {
      const eventName =
        viewMode === "weekly"
          ? "Consult Range Selected"
          : "Consult Day Selected";
      updateRangeOffset(option.offset, eventName);
    },
    [updateRangeOffset, viewMode],
  );

  // Reset to current week/day
  const handleResetRange = useCallback(() => {
    const eventName =
      viewMode === "weekly" ? "Consult Week Reset" : "Consult Day Reset";
    updateRangeOffset(0, eventName);
  }, [updateRangeOffset, viewMode]);

  // Toggle between daily and weekly view
  const toggleViewMode = useCallback(() => {
    const newViewMode = viewMode === "daily" ? "weekly" : "daily";

    trackEvent(
      "View Mode Changed",
      {
        "Previous View": viewMode,
        "New View": newViewMode,
      },
      session,
    );

    setRangeOffset(0);
    setViewMode(newViewMode);
  }, [viewMode, session]);

  return {
    // State
    viewMode,
    setViewMode,
    rangeOffset,
    startDate,
    endDate,
    viewLabel,
    isNextWeek,
    formattedStartDate,
    formattedEndDate,
    currentDateRange,
    setCurrentDateRange,
    rangeOptions,
    minOffset,

    // Actions
    updateRangeOffset,
    handleShiftRange,
    handleRangeSelect,
    handleResetRange,
    toggleViewMode,
  };
};
